// using console gets flagged, this is the ONE place we should allow it.
/* eslint-disable no-console */
import { Logger } from "@amzn/xb-hooks";

const toNumber = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  // Take the first hex digit
  return Math.abs(hash) % 255;
};

const toLightHexDigits = (str: string) => {
  const num = toNumber(str) * 0.4;
  return Math.round(num).toString(16).padStart(2, "0");
};

const toDarkHexDigits = (str: string) => {
  const num = toNumber(str) * 0.4;
  return Math.round(255 - num)
    .toString(16)
    .padStart(2, "f");
};

// since console.count only increments by 1
const simpleCountStorage: Record<string, number> = {};

/**
 * The consoleLogger is only meant to be used when in a non-customer-facing environment.
 *
 * We've noticed that the distributed nature of our app has made it hard to find exactly
 * who is sending any particular metric. This logger will label all messages with the
 * experience provider and component name, making it easier to find the source of the message
 * as well as making it possible to filter the logs to only show the desired text.
 *
 * Additionally, it will add a unique color to each of these labels to help visually distinguish
 * the different labels.
 *
 * @param xpsInfo - information about the current XPS
 */
export const createConsoleLogger = ({
  xpsName: xps = "-",
  componentName: component = ">", //these default make a prefix of [-->]
}: {
  xpsName?: string;
  componentName?: string;
}): Logger => {
  // generate a color code that will be consistent for different xps/component combos
  const seeds = [`${xps}${component}`, xps, component];
  const lightColorCode = seeds.map(toLightHexDigits).join("");
  const darkColorCode = seeds.map(toDarkHexDigits).join("");
  const attributionStyle = `color: light-dark(#${lightColorCode},#${darkColorCode})`;
  const standardMessageStyle = ";";
  const italicMessageStyle = "font-style:italic;font-weight:lighter;";
  const boldMessageStyle = "font-weight:bolder;";

  return {
    count: (metricName, count = 1) => {
      if (metricName in simpleCountStorage) {
        simpleCountStorage[metricName] += count;
      } else {
        simpleCountStorage[metricName] = count;
      }
      const message = `${metricName}: ${simpleCountStorage[metricName]}`;
      console.log(`[%c${xps}-${component}] %c%s`, attributionStyle, standardMessageStyle, message);
    },
    time: (metricName, elapsedTime) => {
      const message = `${metricName}: ${elapsedTime}ms`;
      console.log(`[%c${xps}-${component}] %c%s`, attributionStyle, standardMessageStyle, message);
    },
    info: (message: any, ...rest) =>
      console.info(`%c[${xps}-${component}] %c%s`, attributionStyle, standardMessageStyle, message, ...rest),
    debug: (message: any, ...rest) =>
      console.debug(`%c[${xps}-${component}] %c%s`, attributionStyle, italicMessageStyle, message, ...rest),
    warn: (message: any, ...rest) =>
      console.warn(`%c[${xps}-${component}] %c%s`, attributionStyle, standardMessageStyle, message, ...rest),
    error: (message: any, ...rest) =>
      console.error(`%c[${xps}-${component}] %c%s`, attributionStyle, standardMessageStyle, message, ...rest),
    fatal: (message: any, ...rest) =>
      console.error(`%c[${xps}-${component}] %c%s`, attributionStyle, boldMessageStyle, message, ...rest),
  };
};
